// @flow

/**
 * Prompt Footer Component
 * Generic prompt input area with Ask button
 */

import React from 'react';
import styled from 'styled-components';
import { breakpoints } from '../../../styles/variables';
import { Button } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { useOrganisation } from '../context';

const FooterRow = styled.div`
  gap: 0.75rem;
  align-items: stretch;
  display: flex;
`;

const PanelFooter = styled.div`
  background: var(--main-background);
  display: flex;
  justify-content: flex-end;
  border-radius: 0;

  @media (max-width: ${breakpoints.PHONE_SIZE}) {
    padding: 1rem;
  }
`;

const PromptInputWrapper = styled.div.attrs(() => ({
  className: 'bp3-input-group bp3-fill',
}))`
  width: 100%;
  position: relative;
  display: flex;
  align-items: stretch;
  border-radius: 10px;

  /* Use an overlay inset focus ring so it can't be clipped by overflow:hidden ancestors
     and also won't be hidden behind the textarea's background. */
  &:focus-within::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0 0 0 1px var(--primary-hover);
    pointer-events: none;
    z-index: 2;
  }

  /* Ensure the overlay ring can sit above the input/button visually. */
  > * {
    position: relative;
    z-index: 1;
  }

  --prompt-inset: 0.5rem;
  /* Fallback reserved width; this will be overridden by runtime measurement. */
  --prompt-right-element-width: 4.75rem;
  --prompt-button-height: 2rem;
`;

const PromptTextArea = styled.textarea.attrs(() => ({
  className: 'bp3-input',
  rows: 1,
  wrap: 'soft',
}))`
  width: 100%;
  font-family: var(--fonts-default);
  resize: none;
  overflow-y: hidden;

  /* Reserve space for the in-field Ask button */
  padding-right: calc(var(--prompt-right-element-width) + (var(--prompt-inset) * 2));

  /* Ensure this overrides Blueprint's default .bp3-input padding rules */
  &&& {
    padding-right: calc(var(--prompt-right-element-width) + (var(--prompt-inset) * 2));
  }

  /* Ensure long tokens wrap before reaching the reserved area */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;

  /* Let the wrapper provide the “input group” feel */
  display: block;
  height: auto;
  min-height: 0;

  /* Disable Blueprint's outer focus shadow; wrapper handles focus indication. */
  &&&:focus {
    box-shadow: none;
    outline: none;
  }
`;

const RightElement = styled.div`
  position: absolute;
  right: var(--prompt-inset);
  bottom: var(--prompt-inset);
  transform: translate(8px, 8px);
  display: flex;
  align-items: center; /* Center children vertically for consistent text alignment */
  gap: 1ch; /* ~1 character width gap between status text and button */
  pointer-events: none;

  /* Re-enable interaction on the actual button */
  > * {
    pointer-events: auto;
  }
`;

const StatusText = styled.span`
  color: var(--mid-grey);
  font-size: 0.85em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px; /* Prevent excessively long status text from breaking layout */
  pointer-events: none;
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: var(--fonts-default);
`;

const HelperText = styled.div`
  text-align: center;
  font-size: 0.7em; /* ~70% of PromptInput text size */
  color: var(--mid-grey);
  margin-top: 0.25rem;
`;

const AttributionLink = styled.a`
  &&& {
    color: inherit;
    text-decoration: none;
    cursor: pointer;
  }
  &&&:hover,
  &&&:visited,
  &&&:active {
    color: inherit;
    text-decoration: none;
  }
`;

const WarningText = styled.div`
  text-align: center;
  font-size: 0.8em;
  color: var(--warning); /* expect --warning to be defined; falls back if not */
  margin-bottom: 0.5rem;
`;

const SendButton = styled(Button)`
  /* Increase specificity so we reliably override Blueprint button styles. */
  &&& {
    height: var(--prompt-button-height);
    min-height: var(--prompt-button-height);
    min-width: 3.0rem;
    border-radius: 8px;
    font-weight: 300;
    box-shadow: none;

    background: var(--primary);
    color: var(--button-text-primary);
    border: 1px solid var(--primary);
  }

  &&&:hover:not([disabled]) {
    background: var(--primary);
    color: var(--button-text-primary);
    border-color: var(--primary-hover);
  }

  &&&:active:not([disabled]) {
    background: var(--primary);
    color: var(--button-text-primary);
    border-color: var(--primary-hover);
  }

  &&&[disabled] {
   background: var(--button-inactive-background);
    color: var(--button-text-primary);
    border-color: var(--button-inactive-background);
  }
`;

type PromptFooterProps = {
  inputRef?: React$Ref<any>,
  value: string,
  onChange: (e: SyntheticInputEvent<HTMLTextAreaElement>) => void,
  onKeyDown?: (e: SyntheticKeyboardEvent<HTMLTextAreaElement>) => void,
  isLoading?: boolean,
  onSend: () => void,
  placeholder?: string,
  sendDisabled?: boolean,
  notice?: string,
  helperText?: string,
  attribution?: string,
  askButtonText?: string,
  sendDisabledTooltip?: string,
  /** Show spinner on the Ask button (independent of isLoading/streaming state) */
  showSpinner?: boolean,
  /** Status text displayed to the left of the Ask button */
  statusText?: string,
  /** Called when the textarea receives focus */
  onFocus?: () => void,
};

const PromptFooter = ({
  inputRef,
  value,
  onChange,
  onKeyDown,
  isLoading = false,
  onSend,
  placeholder = 'How can I help you? (shift + enter for new line)',
  sendDisabled = false,
  notice,
  helperText,
  attribution,
  askButtonText = 'Ask',
  sendDisabledTooltip,
  showSpinner = false,
  statusText,
  onFocus,
}: PromptFooterProps) => {
  const isButtonDisabled = sendDisabled || !String(value || '').trim();

  // Compute dynamic default helper text using organisation context
  const orgContext = useOrganisation();
  const organisationName = orgContext?.organisationName || 'your';
  const defaultHelperText = `Aura can make mistakes. Aura Vision doesn't use ${organisationName} workspace data to train its models.`;
  const resolvedHelperText = helperText ?? defaultHelperText;
  const defaultAttribution = (
    <AttributionLink href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer">
      Weather data by Open-Meteo.com
    </AttributionLink>
  );
  const resolvedAttribution = attribution ?? defaultAttribution;

  const internalInputRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const rightElementRef = React.useRef(null);
  const setInputRef = React.useCallback((el) => {
    internalInputRef.current = el;
    if (!inputRef) return;
    // Support both callback refs and ref objects
    if (typeof inputRef === 'function') {
      inputRef(el);
    } else if (typeof inputRef === 'object') {
      // $FlowFixMe[prop-missing] - generic ref object
      inputRef.current = el;
    }
  }, [inputRef]);

  const resizeToFit = React.useCallback(() => {
    const el = internalInputRef.current;
    if (!el) return;

    // Reset height so scrollHeight reflects the full content height.
    el.style.height = 'auto';

    const cs = window.getComputedStyle(el);
    const lineHeightRaw = cs.lineHeight;
    const lineHeight = Number.parseFloat(lineHeightRaw) || 20;
    const paddingTop = Number.parseFloat(cs.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(cs.paddingBottom) || 0;
    const borderTop = Number.parseFloat(cs.borderTopWidth) || 0;
    const borderBottom = Number.parseFloat(cs.borderBottomWidth) || 0;

    const maxHeight = (lineHeight * 5) + paddingTop + paddingBottom + borderTop + borderBottom;
    const nextHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${String(nextHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  React.useLayoutEffect(() => {
    resizeToFit();
  }, [value, resizeToFit]);

  const syncRightElementWidth = React.useCallback(() => {
    const wrapperEl = wrapperRef.current;
    const rightEl = rightElementRef.current;
    if (!wrapperEl || !rightEl) return;

    // Reserve at least the full rendered width of the right element.
    // We also include a small safety buffer so characters don't graze the button.
    const width = rightEl.offsetWidth || 0;
    const buffer = 1; // px
    if (width > 0) {
      wrapperEl.style.setProperty('--prompt-right-element-width', `${String(width + buffer)}px`);
    }
  }, []);

  // Show spinner if explicitly requested via showSpinner OR if isLoading (streaming)
  const displaySpinner = showSpinner || isLoading;

  React.useLayoutEffect(() => {
    syncRightElementWidth();
  }, [askButtonText, isLoading, isButtonDisabled, sendDisabledTooltip, showSpinner, statusText, syncRightElementWidth]);
  
  const sendButton = (
    <SendButton
      key={displaySpinner ? 'loading' : 'idle'}
      text={askButtonText}
      intent="primary"
      onClick={onSend}
      loading={displaySpinner}
      disabled={isButtonDisabled}
    />
  );

  return (
    <PanelFooter>
      <FooterContent>
        {!!notice && <WarningText role="alert">{notice}</WarningText>}
        <FooterRow style={{ width: '100%' }}>
          <PromptInputWrapper ref={wrapperRef}>
            <PromptTextArea
              ref={setInputRef}
              value={value}
              onChange={(e) => {
                onChange(e);
                // Resize after change for immediate feedback even if parent state update lags.
                // (Safe: value-driven effect will also run.)
                requestAnimationFrame(resizeToFit);
                requestAnimationFrame(syncRightElementWidth);
              }}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              placeholder={placeholder}
              disabled={isLoading}
              aria-label="Prompt"
            />
            <RightElement ref={rightElementRef}>
              {!!statusText && <StatusText>{statusText}</StatusText>}
              {sendDisabledTooltip && isButtonDisabled ? (
                <Tooltip2 content={sendDisabledTooltip} position="top">
                  {sendButton}
                </Tooltip2>
              ) : (
                sendButton
              )}
            </RightElement>
          </PromptInputWrapper>
        </FooterRow>
        {(resolvedHelperText || resolvedAttribution) && (
          <HelperText>
            {resolvedHelperText}
            {resolvedHelperText && resolvedAttribution && ' '}
            {resolvedAttribution}
          </HelperText>
        )}
      </FooterContent>
    </PanelFooter>
  );
};

export default PromptFooter;
